import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import logo from '../assets/logo.png'

function Home() {
  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1'
          entry.target.style.transform = 'translateY(0)'
        }
      })
    }, observerOptions)

    // Observe all animated sections
    document.querySelectorAll('.fade-in-section').forEach(el => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(30px)'
      el.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out'
      observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <div style={{
      background: '#0a0a0a',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Navigation Header */}
      <nav style={{
        background: 'rgba(10, 10, 10, 0.8)',
        backdropFilter: 'saturate(180%) blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{
          maxWidth: '980px',
          margin: '0 auto',
          padding: '0 22px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none'
          }}>
            <img
              src={logo}
              alt="Krinos Logo"
              style={{ height: '40px', width: 'auto' }}
            />
            <span style={{
              fontSize: '21px',
              fontWeight: '600',
              color: '#fff',
              letterSpacing: '-0.5px'
            }}>
              Krinos
            </span>
          </Link>
          <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
            <Link to="/login" style={{
              color: '#e5e5e5',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '400',
              transition: 'color 0.3s ease'
            }}>
              Sign In
            </Link>
            <Link to="/signup" style={{
              color: '#fff',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              background: '#0071e3',
              padding: '8px 16px',
              borderRadius: '980px',
              transition: 'background 0.3s ease'
            }}>
              Try it free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        padding: '120px 22px 100px',
        textAlign: 'center',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: '80px',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#fff',
            lineHeight: '1.05',
            letterSpacing: '-3px',
            WebkitFontSmoothing: 'antialiased'
          }}>
            Stop guessing.
          </h1>
          <h1 style={{
            fontSize: '80px',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: '1.05',
            letterSpacing: '-3px'
          }}>
            Start winning trust.
          </h1>

          <p style={{
            fontSize: '28px',
            color: '#b4b4b4',
            marginBottom: '40px',
            lineHeight: '1.4',
            fontWeight: '400',
            letterSpacing: '-0.5px',
            maxWidth: '820px',
            margin: '0 auto 48px'
          }}>
            The professional HVAC assessment platform that helps contractors make honest recommendations and homeowners make confident decisions.
          </p>

          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            marginBottom: '80px',
            flexWrap: 'wrap'
          }}>
            <Link to="/signup" style={{
              fontSize: '17px',
              fontWeight: '500',
              padding: '14px 28px',
              background: '#0071e3',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '980px',
              transition: 'background 0.3s ease'
            }}>
              Start free trial
            </Link>
          </div>

          {/* Dual Value Props */}
          <div className="fade-in-section" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
            gap: '24px',
            marginTop: '80px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '48px 32px',
              borderRadius: '18px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              textAlign: 'left',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👨‍💼</div>
              <h3 style={{ fontSize: '24px', marginBottom: '12px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                For Contractors
              </h3>
              <p style={{ color: '#b4b4b4', fontSize: '17px', lineHeight: '1.5', fontWeight: '400' }}>
                Close more deals with transparent, data-backed assessments that homeowners trust.
              </p>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '48px 32px',
              borderRadius: '18px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              textAlign: 'left',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏡</div>
              <h3 style={{ fontSize: '24px', marginBottom: '12px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                For Homeowners
              </h3>
              <p style={{ color: '#b4b4b4', fontSize: '17px', lineHeight: '1.5', fontWeight: '400' }}>
                Get honest, unbiased recommendations based on data, not sales pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Builder Section */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#141414'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '16px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Built on data.<br/>Not sales pressure.
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '80px',
            maxWidth: '700px',
            margin: '0 auto 80px',
            fontWeight: '400',
            lineHeight: '1.4'
          }}>
            Science-based analysis delivers recommendations your customers believe in.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '48px'
          }}>
            {[
              { icon: '📊', title: 'Science-Based Analysis', desc: '20+ factors analyzed per assessment including age-based efficiency degradation, regional climate data, and home envelope conditions.', color: '#667eea' },
              { icon: '💰', title: 'Real Financial Transparency', desc: '10-year cost comparisons, actual ROI projections, and regional rebate calculations customized for your area.', color: '#10b981' },
              { icon: '✅', title: 'Honest Recommendations', desc: 'Sometimes REPAIR. Sometimes REPLACE. Sometimes MAINTAIN. Our algorithm recommends what is actually best.', color: '#f59e0b' }
            ].map((item, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '56px',
                  marginBottom: '24px'
                }}>
                  {item.icon}
                </div>
                <h3 style={{ fontSize: '24px', marginBottom: '16px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                  {item.title}
                </h3>
                <p style={{ color: '#b4b4b4', fontSize: '17px', lineHeight: '1.5', fontWeight: '400' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#0a0a0a'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '80px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            The HVAC industry<br/>has a trust problem.
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
            gap: '32px',
            marginBottom: '64px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '48px',
              borderRadius: '18px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
              <h3 style={{ fontSize: '28px', marginBottom: '24px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                Contractors face
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Homeowners skeptical of replacement recommendations',
                  'Lost deals due to perceived sales bias',
                  'Competitors undercutting with low-ball quotes',
                  'Difficulty justifying premium pricing'
                ].map((item, index) => (
                  <li key={index} style={{ marginBottom: '20px', paddingLeft: '32px', position: 'relative', color: '#b4b4b4', fontSize: '17px', lineHeight: '1.5' }}>
                    <span style={{ position: 'absolute', left: 0, fontSize: '20px', color: '#ef4444' }}>✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '48px',
              borderRadius: '18px',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
              <h3 style={{ fontSize: '28px', marginBottom: '24px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                Homeowners worry
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Am I being upsold unnecessarily?',
                  'Is my system really failing?',
                  'What if I spend $12,000 for nothing?',
                  'Can I trust this contractor?'
                ].map((item, index) => (
                  <li key={index} style={{ marginBottom: '20px', paddingLeft: '32px', position: 'relative', color: '#b4b4b4', fontSize: '17px', lineHeight: '1.5' }}>
                    <span style={{ position: 'absolute', left: 0, fontSize: '20px', color: '#ef4444' }}>✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '56px',
            borderRadius: '18px',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '40px', fontWeight: '600', marginBottom: '16px', color: '#fff', letterSpacing: '-1px' }}>
              Krinos solves both problems.
            </h3>
            <p style={{ fontSize: '21px', color: 'rgba(255,255,255,0.9)', fontWeight: '400' }}>
              With one transparent, data-driven platform.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#141414'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '16px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Professional assessments<br/>in 3 minutes.
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '80px',
            fontWeight: '400'
          }}>
            Simple for you. Impressive for your customers.
          </p>

          <div style={{
            display: 'grid',
            gap: '64px'
          }}>
            {[
              {
                num: '1',
                title: 'Gather Information',
                time: '30 seconds',
                desc: 'System age, repair costs, customer comfort issues, home envelope factors (insulation, windows, ductwork). Takes less time than a traditional quote.',
                gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              },
              {
                num: '2',
                title: 'Instant AI Analysis',
                time: 'Real-time',
                desc: 'Calculates efficiency degradation, projects 10-year maintenance costs, compares repair vs. replace scenarios, identifies available regional rebates.',
                gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              },
              {
                num: '3',
                title: 'Share Results',
                time: 'Instant delivery',
                desc: 'Color-coded recommendation, detailed cost breakdown with charts, energy savings projections, rebate eligibility, and a shareable link for homeowners to review.',
                gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              }
            ].map((step, index) => (
              <div key={index} style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                gap: '40px',
                alignItems: 'start'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '24px',
                  background: step.gradient,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '48px',
                  fontWeight: '700',
                  flexShrink: 0,
                  letterSpacing: '-2px'
                }}>
                  {step.num}
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#b4b4b4', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '500' }}>
                    {step.time}
                  </div>
                  <h3 style={{ fontSize: '32px', marginBottom: '16px', color: '#fff', fontWeight: '600', letterSpacing: '-1px' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: '#b4b4b4', fontSize: '19px', lineHeight: '1.5', fontWeight: '400' }}>
                    {step.desc}
                  </p>
                  {index === 2 && (
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginTop: '24px'
                    }}>
                      {['REPLACE', 'CONSIDER', 'REPAIR', 'MAINTAIN'].map((badge, i) => (
                        <span key={i} style={{
                          padding: '8px 16px',
                          background: i === 0 ? 'rgba(239, 68, 68, 0.15)' : i === 1 ? 'rgba(245, 158, 11, 0.15)' : i === 2 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                          color: i === 0 ? '#ef4444' : i === 1 ? '#f59e0b' : i === 2 ? '#22c55e' : '#3b82f6',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#0a0a0a'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '16px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Everything you need.
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '80px',
            fontWeight: '400'
          }}>
            A complete platform built specifically for HVAC professionals.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: '✅', title: 'Instant Credibility', desc: 'Data-backed recommendations homeowners believe in' },
              { icon: '📈', title: 'Higher Close Rates', desc: 'Transparent pricing reduces objections' },
              { icon: '⚡', title: '3-Minute Assessments', desc: 'Complete professional analysis on-site' },
              { icon: '💵', title: 'Regional Intelligence', desc: 'Automatic rebate calculations for your area' },
              { icon: '📊', title: 'Customer History', desc: 'Track assessments and build relationships' },
              { icon: '👥', title: 'Team Management', desc: 'Multiple technicians, unified brand' },
              { icon: '📱', title: 'Works Anywhere', desc: 'Mobile-friendly, no equipment needed' },
              { icon: '🤖', title: 'AI-Powered', desc: 'Professional summaries explain recommendations' },
              { icon: '🎯', title: 'Unbiased Analysis', desc: 'Algorithm-based, not sales-driven' }
            ].map((feature, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '32px',
                borderRadius: '18px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>{feature.icon}</div>
                <h3 style={{ fontSize: '19px', marginBottom: '8px', color: '#fff', fontWeight: '600' }}>
                  {feature.title}
                </h3>
                <p style={{ color: '#b4b4b4', fontSize: '15px', lineHeight: '1.5', fontWeight: '400' }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Section */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#141414'
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Tailored to your region.
          </h2>
          <p style={{
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '64px',
            lineHeight: '1.4',
            fontWeight: '400'
          }}>
            Customized to your business, regional rebates, climate zones, and local utility providers.
          </p>

          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            padding: '48px',
            borderRadius: '18px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            textAlign: 'left'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '24px'
            }}>
              {[
                'Climate zone calculations',
                'Utility provider rates',
                'Local rebate programs',
                'State incentives',
                'Regional HVAC standards',
                'Seasonal efficiency factors'
              ].map((item, index) => (
                <div key={index} style={{ paddingLeft: '32px', position: 'relative', color: '#b4b4b4', fontSize: '17px' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '20px' }}>✓</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Pays for itself with<br/>just one extra deal.
          </h2>
          <p style={{
            fontSize: '24px',
            marginBottom: '64px',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: '400'
          }}>
            Most contractors see ROI within the first week.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '48px',
            marginBottom: '32px'
          }}>
            {[
              { stat: '35%', label: 'Average close rate increase' },
              { stat: '$8K', label: 'Average replacement value' },
              { stat: '1,233%', label: 'Typical monthly ROI' }
            ].map((item, index) => (
              <div key={index}>
                <div style={{ fontSize: '64px', fontWeight: '700', marginBottom: '8px', color: '#fff', letterSpacing: '-3px' }}>{item.stat}</div>
                <div style={{ fontSize: '17px', color: 'rgba(255,255,255,0.8)', fontWeight: '400' }}>{item.label}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '21px', color: 'rgba(255,255,255,0.9)', fontWeight: '400' }}>
            Just 2 extra deals per month = $16,000 in revenue for $1,299 investment
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#0a0a0a'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Simple pricing.
          </h2>
          <p style={{
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '64px',
            fontWeight: '400'
          }}>
            No hidden fees. No contracts.
          </p>

          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            padding: '64px 48px',
            borderRadius: '24px',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600',
              letterSpacing: '0.5px'
            }}>
              PROFESSIONAL PLAN
            </div>

            <div style={{ marginBottom: '40px' }}>
              <div style={{ fontSize: '72px', fontWeight: '700', color: '#fff', marginBottom: '8px', letterSpacing: '-3px' }}>
                $1,299
              </div>
              <div style={{ fontSize: '21px', color: '#b4b4b4', fontWeight: '400', marginBottom: '8px' }}>
                per month
              </div>
              <div style={{ fontSize: '19px', color: '#10b981', fontWeight: '600' }}>
                7-Day Free Trial
              </div>
            </div>

            <div style={{
              textAlign: 'left',
              marginBottom: '40px',
              maxWidth: '480px',
              margin: '0 auto 40px'
            }}>
              {[
                '500 assessments included per month',
                '$0.39 per additional assessment',
                'Unlimited team members',
                'Customer history & reports',
                'Regional rebate calculator',
                'AI-powered insights',
                'Email support'
              ].map((feature, index) => (
                <div key={index} style={{ marginBottom: '16px', paddingLeft: '32px', position: 'relative', fontSize: '17px', color: '#b4b4b4' }}>
                  <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '20px' }}>✓</span>
                  {feature}
                </div>
              ))}
            </div>

            <Link to="/signup" style={{
              fontSize: '17px',
              fontWeight: '500',
              padding: '16px 32px',
              background: '#0071e3',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '980px',
              display: 'inline-block',
              transition: 'background 0.3s ease'
            }}>
              Start free trial
            </Link>

            <p style={{ marginTop: '24px', fontSize: '14px', color: '#b4b4b4' }}>
              No credit card required for trial
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#141414'
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '56px',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '16px',
            color: '#fff',
            letterSpacing: '-2px',
            lineHeight: '1.1'
          }}>
            Common questions.
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '24px',
            color: '#b4b4b4',
            marginBottom: '64px',
            fontWeight: '400'
          }}>
            Everything you need to know.
          </p>

          <div style={{
            display: 'grid',
            gap: '24px'
          }}>
            {[
              {
                q: 'How many team members can I add?',
                a: 'Unlimited. Add as many technicians, office staff, or managers as you need—all included in your subscription.'
              },
              {
                q: 'What if I have multiple locations with different rebates or utility rates?',
                a: 'Each subscription is configured for one specific region (climate zone, utility provider, and rebate programs). If you service multiple areas with different rebates, utility rates, or climate zones, you will need a separate subscription for each region to ensure accurate calculations.'
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. No contracts, no commitments. Cancel anytime from your billing dashboard. Your access continues until the end of your billing period.'
              },
              {
                q: 'What happens if I exceed 500 assessments?',
                a: 'You are automatically charged $0.39 per additional assessment. No interruptions, no caps. We bill you at the end of the month for any overages.'
              },
              {
                q: 'Do homeowners see the assessments?',
                a: 'Only if you share them. You can generate a secure, time-limited link that expires after 30 days. Homeowners can view the full assessment report through that link.'
              },
              {
                q: 'Is my data secure?',
                a: 'Absolutely. All data is encrypted in transit and at rest. We use industry-standard security practices and are hosted on enterprise-grade infrastructure.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '32px',
                borderRadius: '18px',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <h3 style={{
                  fontSize: '21px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: '#fff',
                  letterSpacing: '-0.5px'
                }}>
                  {item.q}
                </h3>
                <p style={{
                  fontSize: '17px',
                  color: '#b4b4b4',
                  lineHeight: '1.6',
                  fontWeight: '400',
                  margin: 0
                }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Homeowners */}
      <section className="fade-in-section" style={{
        padding: '120px 22px',
        background: '#0a0a0a'
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '700',
            marginBottom: '32px',
            color: '#fff',
            textAlign: 'center',
            letterSpacing: '-1.5px',
            lineHeight: '1.1'
          }}>
            If you are a homeowner<br/>reading this...
          </h2>

          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            padding: '48px',
            borderRadius: '18px',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <p style={{ fontSize: '21px', color: '#b4b4b4', lineHeight: '1.6', marginBottom: '32px', fontWeight: '400' }}>
              Good news. If your HVAC contractor uses Krinos, you are working with a professional who values transparency over pressure tactics.
            </p>

            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#fff', fontWeight: '600', letterSpacing: '-0.5px' }}>
                Our platform helps contractors give you:
              </h3>
              <div style={{ display: 'grid', gap: '16px' }}>
                {[
                  'Honest, data-driven recommendations',
                  'Clear cost comparisons (repair vs. replace)',
                  'Real 10-year financial projections',
                  'Available rebates for your area',
                  'No hidden agendas or sales pressure'
                ].map((item, index) => (
                  <div key={index} style={{ paddingLeft: '32px', position: 'relative', color: '#b4b4b4', fontSize: '17px' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '20px' }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '32px',
              borderRadius: '12px',
              borderLeft: '4px solid #10b981'
            }}>
              <p style={{ fontSize: '17px', color: '#b4b4b4', lineHeight: '1.6', margin: 0, fontWeight: '400' }}>
                <strong style={{ color: '#fff' }}>Not all contractors use Krinos yet.</strong> If you want an honest assessment, ask your contractor if they offer Krinos-powered evaluations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="fade-in-section" style={{
        padding: '140px 22px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '64px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#fff',
            letterSpacing: '-2.5px',
            lineHeight: '1.05'
          }}>
            Ready to build trust<br/>and close more deals?
          </h2>
          <p style={{
            fontSize: '24px',
            marginBottom: '48px',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: '400',
            lineHeight: '1.4'
          }}>
            Join the HVAC contractors who are winning with transparency.
          </p>

          <Link to="/signup" style={{
            fontSize: '17px',
            fontWeight: '500',
            padding: '16px 40px',
            background: '#fff',
            color: '#667eea',
            textDecoration: 'none',
            borderRadius: '980px',
            display: 'inline-block',
            transition: 'all 0.3s ease'
          }}>
            Start free trial
          </Link>

          <div style={{
            marginTop: '48px',
            display: 'flex',
            gap: '40px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.8)'
          }}>
            <div>🔒 Secure payment via Stripe</div>
            <div>✓ No setup fees</div>
            <div>✓ Cancel anytime</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#0a0a0a',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '64px 22px 40px'
      }}>
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '48px',
            marginBottom: '48px'
          }}>
            <div>
              <div style={{
                fontSize: '21px',
                fontWeight: '600',
                marginBottom: '16px',
                color: '#fff'
              }}>
                Krinos
              </div>
              <p style={{ fontSize: '14px', color: '#b4b4b4', lineHeight: '1.6', fontWeight: '400' }}>
                Professional HVAC assessment platform for contractors who value transparency.
              </p>
            </div>

            <div>
              <h4 style={{ fontSize: '14px', marginBottom: '16px', fontWeight: '600', color: '#fff', letterSpacing: '0.5px' }}>Product</h4>
              <div style={{ display: 'grid', gap: '12px', fontSize: '14px', color: '#b4b4b4' }}>
                <a href="#how-it-works" style={{ color: '#b4b4b4', textDecoration: 'none', fontWeight: '400' }}>How It Works</a>
                <Link to="/signup" style={{ color: '#b4b4b4', textDecoration: 'none', fontWeight: '400' }}>Pricing</Link>
              </div>
            </div>

            <div>
              <h4 style={{ fontSize: '14px', marginBottom: '16px', fontWeight: '600', color: '#fff', letterSpacing: '0.5px' }}>Company</h4>
              <div style={{ display: 'grid', gap: '12px', fontSize: '14px', color: '#b4b4b4' }}>
                <Link to="/login" style={{ color: '#b4b4b4', textDecoration: 'none', fontWeight: '400' }}>Sign In</Link>
                <Link to="/signup" style={{ color: '#b4b4b4', textDecoration: 'none', fontWeight: '400' }}>Get Started</Link>
              </div>
            </div>

            <div>
              <h4 style={{ fontSize: '14px', marginBottom: '16px', fontWeight: '600', color: '#fff', letterSpacing: '0.5px' }}>Contact</h4>
              <div style={{ fontSize: '14px', color: '#b4b4b4', fontWeight: '400' }}>
                support@krinos.ai
              </div>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            paddingTop: '24px',
            textAlign: 'center',
            fontSize: '12px',
            color: '#86868b'
          }}>
            Copyright © 2025 Krinos. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
